import { router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Badge } from '@/components/ui/badge'
import { Building2, Users } from 'lucide-react'
import { formatDate } from '@/lib/utils'

type BackofficeAccount = {
  id: string
  name: string
  slug: string
  type: 'IndividualAccount' | 'TeamAccount'
  deleted: boolean
  members_count: number
  created_at: string
}

type Props = {
  accounts: BackofficeAccount[]
  search: string
  pagination: PaginationMeta
}

export default function BackofficeAccountsIndex({ accounts, search, pagination }: Props) {
  return (
    <BackofficeLayout
      title="Workspaces"
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Workspaces' },
      ]}
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Workspaces</h1>
          <p className="text-sm text-muted-foreground">{pagination.total} total</p>
        </div>
      </div>

      <SearchInput value={search} placeholder="Search by name or slug..." />

      <div className="mt-4">
        {accounts.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <Building2 className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No workspaces found</h3>
            <p className="text-sm text-muted-foreground">Workspaces are created when users register.</p>
          </div>
        ) : (
          <div className="border rounded-lg">
            {accounts.map((a, i) => (
              <div
                key={a.id}
                className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                onClick={() => router.visit(`/backoffice/accounts/${a.id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-xs font-medium text-muted-foreground">
                      {a.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{a.name}</span>
                      <Badge variant="outline" className="text-[10px] font-normal shrink-0">
                        {a.type === 'IndividualAccount' ? 'Personal' : 'Team'}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">{a.slug}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0 ml-4">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="w-3 h-3" />
                    {a.members_count}
                  </div>
                  <div className="text-xs text-muted-foreground">{formatDate(a.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
